'use client'

import { AnimatePresence, motion } from 'framer-motion'
import { useTranslations } from 'next-intl'
import type { ReactNode } from 'react'

import { TickArrowIcon } from '@/components/shell-icons'
import { Button } from '@/components/ui/button'
import { RollingNumber } from '@/components/ui/rolling-number'
import { SegmentedControl } from '@/components/ui/segmented-control'
import { Skeleton } from '@/components/ui/skeleton'
import { ExchangeIcon } from '@/components/widget/exchange-icon'
import type { PnlTimeframe } from '@/features/dashboard/lib/account-summary-display'
import {
  HOME_EASE,
  HOME_LIST,
  HOME_ROW,
  HOME_SPRING,
  HomePanel,
} from '@/features/home/components/home-motion'
import { HomeSparkline } from '@/features/home/components/home-sparkline'
import type { HomeSnapshot } from '@/features/home/hooks/use-home-snapshot'
import { useNumberFormat } from '@/hooks/use-number-format'
import { tradeVenueToExchangeIconKey } from '@/lib/configs/exchange-registry'
import { cn } from '@/lib/utils'

const TIMEFRAMES: readonly PnlTimeframe[] = ['1D', '1W', '1M', 'ALL']

interface StatTileProps {
  label: string
  value: ReactNode
  hint?: ReactNode
  tone?: 'up' | 'down' | 'warn'
  onClick?: () => void
}

function StatTile({ label, value, hint, tone, onClick }: StatTileProps) {
  return (
    <motion.button
      type="button"
      variants={HOME_ROW}
      onClick={onClick}
      disabled={!onClick}
      whileHover={onClick ? { y: -2 } : undefined}
      whileTap={onClick ? { scale: 0.98 } : undefined}
      transition={HOME_SPRING}
      className="group bg-foreground/[0.03] hover:bg-foreground/[0.06] flex min-w-0 cursor-pointer flex-col gap-1 rounded-md px-3 py-2.5 text-left transition-colors disabled:cursor-default"
    >
      <span className="text-foreground/50 flex items-center justify-between gap-2 text-xs">
        {label}
        {onClick && (
          <span
            aria-hidden="true"
            className="text-foreground/30 -translate-x-1 opacity-0 transition-[opacity,translate] duration-200 group-hover:translate-x-0 group-hover:opacity-100"
          >
            →
          </span>
        )}
      </span>
      <span
        className={cn(
          'text-foreground truncate text-lg leading-6 font-medium tracking-tight',
          tone === 'up' && 'text-chart-mint',
          tone === 'down' && 'text-chart-red',
          tone === 'warn' && 'text-ox-gold'
        )}
      >
        {value}
      </span>
      {hint && <span className="text-foreground/40 truncate text-[11px]">{hint}</span>}
    </motion.button>
  )
}

interface HomePortfolioProps {
  snapshot: HomeSnapshot
  timeframe: PnlTimeframe
  onTimeframeChange: (timeframe: PnlTimeframe) => void
  onOpenTrading: () => void
  onOpenHedge: () => void
  onConnectExchange: () => void
  className?: string
}

/**
 * The portfolio at a glance: total value across every connected account (rolling digits), PnL
 * for the chosen window, the equity curve, and the four numbers a trader checks first —
 * positions, open orders, unrealized PnL and leverage — each a shortcut to where it lives.
 */
export function HomePortfolio({
  snapshot,
  timeframe,
  onTimeframeChange,
  onOpenTrading,
  onOpenHedge,
  onConnectExchange,
  className,
}: HomePortfolioProps) {
  const t = useTranslations('trade.home.portfolio')
  const fmt = useNumberFormat()
  const { portfolio, risk, openOrderCount, automations } = snapshot
  const noAccounts = !portfolio.isAccountsLoading && portfolio.accountCount === 0
  const valuePending = portfolio.isAccountsLoading || (!noAccounts && portfolio.equityUsd === null)
  const pnlUp = portfolio.pnlUsd >= 0
  const leverage = portfolio.netLeverage

  return (
    <HomePanel
      title={t('title')}
      className={className}
      aside={
        !noAccounts && (
          <>
            <div className="hidden items-center -space-x-1 sm:flex">
              {portfolio.venues.map((venue) => (
                <span
                  key={venue}
                  className="border-card bg-card flex size-5 items-center justify-center overflow-hidden rounded-full border"
                >
                  <ExchangeIcon exchange={tradeVenueToExchangeIconKey(venue)} className="size-4" />
                </span>
              ))}
            </div>
            <span className="text-foreground/50 hidden text-xs whitespace-nowrap sm:inline">
              {t('accounts', { count: portfolio.accountCount })}
            </span>
            <SegmentedControl
              layoutId="home-portfolio-timeframe"
              size="xs"
              value={timeframe}
              onChange={onTimeframeChange}
              options={TIMEFRAMES.map((value) => ({ value, label: t(`timeframes.${value}`) }))}
            />
          </>
        )
      }
    >
      {noAccounts ? (
        <div className="flex flex-1 flex-col items-start justify-center gap-3 py-6">
          <p className="font-display text-foreground text-2xl tracking-tight">{t('emptyTitle')}</p>
          <p className="text-foreground/55 max-w-md text-sm">{t('emptyHint')}</p>
          <Button size="sm" onClick={onConnectExchange}>
            {t('connect')}
          </Button>
        </div>
      ) : (
        <div className="grid flex-1 grid-cols-1 gap-4 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
          <div className="flex min-w-0 flex-col justify-between gap-4">
            <div className="flex flex-col gap-1.5">
              <span className="text-foreground/50 text-xs">{t('totalValue')}</span>
              {valuePending ? (
                <Skeleton className="h-11 w-56" />
              ) : (
                <RollingNumber
                  text={fmt.usd(portfolio.equityUsd, { sensitive: true })}
                  className="font-display text-foreground text-4xl leading-tight tracking-tight md:text-[2.75rem]"
                />
              )}
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={timeframe}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.2, ease: HOME_EASE }}
                  className={cn(
                    'flex items-center gap-1.5 text-sm font-medium',
                    pnlUp ? 'text-chart-mint' : 'text-chart-red'
                  )}
                >
                  <TickArrowIcon className={cn(!pnlUp && 'rotate-180')} />
                  <span>{fmt.usd(Math.abs(portfolio.pnlUsd), { sensitive: true })}</span>
                  {portfolio.pnlPercent !== null && (
                    <span className="opacity-80">
                      ({fmt.number(Math.abs(portfolio.pnlPercent), { maximumFractionDigits: 2 })}%)
                    </span>
                  )}
                  <span className="text-foreground/40 font-normal">
                    {t(`windowLabel.${timeframe}`)}
                  </span>
                </motion.div>
              </AnimatePresence>
              {portfolio.resolvedCount > 0 && portfolio.resolvedCount < portfolio.accountCount && (
                <span className="text-ox-gold text-[11px]">
                  {t('partial', {
                    resolved: portfolio.resolvedCount,
                    total: portfolio.accountCount,
                  })}
                </span>
              )}
            </div>
            <motion.div
              variants={HOME_LIST}
              initial="hidden"
              animate="show"
              className="grid grid-cols-2 gap-2"
            >
              <StatTile
                label={t('positions')}
                value={risk.items.length}
                hint={
                  risk.flaggedCount > 0
                    ? t('positionsFlagged', { count: risk.flaggedCount })
                    : t('positionsHint')
                }
                tone={risk.flaggedCount > 0 ? 'warn' : undefined}
                onClick={onOpenTrading}
              />
              <StatTile
                label={t('openOrders')}
                value={openOrderCount}
                hint={
                  automations.total > 0
                    ? t('automations', { count: automations.total })
                    : t('openOrdersHint')
                }
                onClick={onOpenTrading}
              />
              <StatTile
                label={t('unrealizedPnl')}
                value={
                  portfolio.unrealizedPnlUsd === null
                    ? '—'
                    : fmt.usd(portfolio.unrealizedPnlUsd, { sensitive: true })
                }
                tone={
                  portfolio.unrealizedPnlUsd === null || portfolio.unrealizedPnlUsd === 0
                    ? undefined
                    : portfolio.unrealizedPnlUsd > 0
                      ? 'up'
                      : 'down'
                }
                hint={t('unrealizedHint')}
              />
              <StatTile
                label={t('leverage')}
                value={
                  leverage === null || !Number.isFinite(leverage)
                    ? '—'
                    : `${fmt.number(Math.abs(leverage), { maximumFractionDigits: 2 })}x`
                }
                tone={leverage !== null && Math.abs(leverage) >= 5 ? 'warn' : undefined}
                hint={t('leverageHint')}
                onClick={onOpenHedge}
              />
            </motion.div>
          </div>
          <div className="relative min-h-40 min-w-0">
            {portfolio.series.length >= 2 ? (
              <HomeSparkline values={portfolio.series} drawKey={timeframe} />
            ) : portfolio.isSeriesPending ? (
              <Skeleton className="h-full w-full" />
            ) : (
              <p className="text-foreground/40 flex h-full items-center justify-center text-xs">
                {t('noHistory')}
              </p>
            )}
          </div>
        </div>
      )}
    </HomePanel>
  )
}
